import { Container, Graphics, BlurFilter } from 'pixi.js';
import { WORLD_RADIUS, WORLD_BORDER_THICKNESS } from '@wooorms/shared';
import type { Game } from './Game';
import { GameLayer } from './Game';
import { BackgroundAmbiance } from './effects/BackgroundAmbiance';
import {
  WORLD_GRID_SIZE,
  WORLD_GRID_COLOR,
  WORLD_GRID_ALPHA,
  WORLD_BACKGROUND_COLOR,
  WORLD_BORDER_COLOR,
  WORLD_BORDER_GLOW_COLOR,
  WORLD_WARNING_DISTANCE,
  WORLD_ORIGIN_MARKER_RADIUS,
  WORLD_ORIGIN_MARKER_COLOR,
  WORLD_ORIGIN_MARKER_ALPHA,
  SNAKE_PRIMARY_COLOR,
  SNAKE_SECONDARY_COLOR,
  SNAKE_EYE_COLOR,
  SNAKE_BELLY_COLOR,
  SNAKE_SCALE_SPOT_COLOR,
  SNAKE_TONGUE_COLOR,
  SNAKE_SEGMENT_COUNT,
  SNAKE_WIDTH_MULTIPLIER,
  SNAKE_SCALE_SIZE_MULTIPLIER,
  SNAKE_UNDULATION_MAGNITUDE,
  SNAKE_UNDULATION_FREQUENCY,
  SNAKE_ANIMATION_SPEED,
  SNAKE_TONGUE_ANIMATION_SPEED,
  SNAKE_TAIL_SEGMENTS,
  GLOW_BLUR_STRENGTH,
  GLOW_BLUR_QUALITY,
  BORDER_GLOW_PULSE_PERIOD,
  BORDER_GLOW_PULSE_AMPLITUDE,
  BORDER_GLOW_PULSE_CENTER,
  BORDER_GLOW_WIDTH_MULTIPLIER,
  INNER_DANGER_LINE_ALPHA,
} from '../constants';

/**
 * World configuration options
 */
export interface WorldConfig {
  /** World radius */
  radius: number;
  /** Grid cell size */
  gridSize: number;
  /** Grid line color */
  gridColor: number;
  /** Grid line alpha */
  gridAlpha: number;
  /** Background color */
  backgroundColor: number;
  /** Border color */
  borderColor: number;
  /** Border glow color */
  borderGlowColor: number;
  /** Distance from border to start warning */
  warningDistance: number;
  /** Giant snake primary color */
  snakePrimaryColor: number;
  /** Giant snake secondary color */
  snakeSecondaryColor: number;
  /** Giant snake eye color */
  snakeEyeColor: number;
}

/**
 * Default world configuration
 */
const DEFAULT_CONFIG: WorldConfig = {
  radius: WORLD_RADIUS,
  gridSize: WORLD_GRID_SIZE,
  gridColor: WORLD_GRID_COLOR,
  gridAlpha: WORLD_GRID_ALPHA,
  backgroundColor: WORLD_BACKGROUND_COLOR,
  borderColor: WORLD_BORDER_COLOR,
  borderGlowColor: WORLD_BORDER_GLOW_COLOR,
  warningDistance: WORLD_WARNING_DISTANCE,
  snakePrimaryColor: SNAKE_PRIMARY_COLOR,
  snakeSecondaryColor: SNAKE_SECONDARY_COLOR,
  snakeEyeColor: SNAKE_EYE_COLOR,
};

/**
 * World renderer - handles background grid, boundary, and edge warnings
 */
export class World {
  /** Reference to the main game instance */
  private game: Game;

  /** World configuration */
  private config: WorldConfig;

  /** Container for world graphics */
  private container: Container;

  /** Grid graphics */
  private gridGraphics: Graphics;

  /** Border graphics */
  private borderGraphics: Graphics;

  /** Border glow graphics (for warning effect) */
  private borderGlowGraphics: Graphics;

  /** Giant snake body graphics */
  private snakeBodyGraphics: Graphics;

  /** Giant snake head graphics */
  private snakeHeadGraphics: Graphics;

  /** Giant snake animation time */
  private snakeAnimTime: number = 0;

  /** Blur filter for glow effect */
  private glowFilter: BlurFilter;

  /** Background ambiance system */
  private ambiance: BackgroundAmbiance;

  /** Current warning intensity (0-1) */
  private warningIntensity: number = 0;

  /** Player distance from center (for warning calculation) */
  private playerDistanceFromCenter: number = 0;

  /** Current grid scale level (multiplier for base grid size based on zoom) */
  private currentGridScale: number = 1;

  constructor(game: Game, config: Partial<WorldConfig> = {}) {
    this.game = game;
    this.config = { ...DEFAULT_CONFIG, ...config };

    this.container = new Container();
    this.container.label = 'world-background';

    this.gridGraphics = new Graphics();
    this.borderGraphics = new Graphics();
    this.borderGlowGraphics = new Graphics();
    this.snakeBodyGraphics = new Graphics();
    this.snakeHeadGraphics = new Graphics();

    this.glowFilter = new BlurFilter({
      strength: GLOW_BLUR_STRENGTH,
      quality: GLOW_BLUR_QUALITY,
    });
    this.borderGlowGraphics.filters = [this.glowFilter];

    // Initialize background ambiance
    this.ambiance = new BackgroundAmbiance(this.config.radius);
  }

  /**
   * Initialize the world renderer
   */
  async init(): Promise<void> {
    // Add container to background layer
    const backgroundLayer = this.game.getLayer(GameLayer.BACKGROUND);
    backgroundLayer.addChild(this.container);

    // Add graphics in order (ambiance behind grid for subtle effect)
    this.container.addChild(this.ambiance.container);
    this.container.addChild(this.gridGraphics);
    this.container.addChild(this.borderGlowGraphics);
    this.container.addChild(this.snakeBodyGraphics);
    this.container.addChild(this.snakeHeadGraphics);
    this.container.addChild(this.borderGraphics);

    // Draw initial world
    this.drawGrid();
    this.drawGiantSnake();
    this.drawBorderGlow();
  }

  /**
   * Draw the background grid
   */
  private drawGrid(): void {
    const { radius, gridSize: baseGridSize, gridColor, gridAlpha } = this.config;
    const g = this.gridGraphics;
    g.clear();

    // Scale grid size based on current zoom level
    // This keeps the grid visually consistent at all zoom levels
    const gridSize = baseGridSize * this.currentGridScale;

    // Calculate grid bounds (slightly larger than world)
    const gridExtent = radius + gridSize;

    // Draw vertical lines
    for (let x = -gridExtent; x <= gridExtent; x += gridSize) {
      // Calculate line length based on circular boundary
      const maxY = Math.sqrt(Math.max(0, gridExtent * gridExtent - x * x));

      g.moveTo(x, -maxY);
      g.lineTo(x, maxY);
    }

    // Draw horizontal lines
    for (let y = -gridExtent; y <= gridExtent; y += gridSize) {
      const maxX = Math.sqrt(Math.max(0, gridExtent * gridExtent - y * y));

      g.moveTo(-maxX, y);
      g.lineTo(maxX, y);
    }

    // Slightly thicker lines for larger grids to maintain visibility
    const lineWidth = Math.min(2, 1 + (this.currentGridScale - 1) * 0.25);
    g.stroke({ width: lineWidth, color: gridColor, alpha: gridAlpha });

    // Draw origin marker (scale with grid for visibility)
    const markerRadius = WORLD_ORIGIN_MARKER_RADIUS * Math.sqrt(this.currentGridScale);
    g.circle(0, 0, markerRadius);
    g.fill({ color: WORLD_ORIGIN_MARKER_COLOR, alpha: WORLD_ORIGIN_MARKER_ALPHA });
  }

  /**
   * Draw the giant snake that encircles the world
   */
  private drawGiantSnake(): void {
    const { radius, snakePrimaryColor, snakeSecondaryColor, snakeEyeColor } = this.config;
    const bodyG = this.snakeBodyGraphics;
    const headG = this.snakeHeadGraphics;
    const borderG = this.borderGraphics;

    bodyG.clear();
    headG.clear();
    borderG.clear();

    // Snake parameters
    const snakeWidth = WORLD_BORDER_THICKNESS * SNAKE_WIDTH_MULTIPLIER;
    const segmentCount = SNAKE_SEGMENT_COUNT;
    const scaleSize = snakeWidth * SNAKE_SCALE_SIZE_MULTIPLIER;

    // Draw snake body segments around the circle
    for (let i = 0; i < segmentCount; i++) {
      const angle = (i / segmentCount) * Math.PI * 2;

      // Undulation effect - snake body slightly moves in/out
      const undulation = Math.sin(angle * SNAKE_UNDULATION_FREQUENCY + this.snakeAnimTime * SNAKE_ANIMATION_SPEED) * SNAKE_UNDULATION_MAGNITUDE;
      const segmentRadius = radius + undulation;

      const x = Math.cos(angle) * segmentRadius;
      const y = Math.sin(angle) * segmentRadius;

      // Alternate colors for scale pattern
      const isEvenSegment = i % 2 === 0;
      const baseColor = isEvenSegment ? snakePrimaryColor : snakeSecondaryColor;

      // Draw main body segment
      bodyG.circle(x, y, snakeWidth / 2);
      bodyG.fill({ color: baseColor, alpha: 1 });

      // Draw scale/belly detail (lighter underside toward center)
      const bellyX = Math.cos(angle) * (segmentRadius - snakeWidth * 0.2);
      const bellyY = Math.sin(angle) * (segmentRadius - snakeWidth * 0.2);
      bodyG.circle(bellyX, bellyY, snakeWidth * 0.25);
      bodyG.fill({ color: SNAKE_BELLY_COLOR, alpha: 0.5 });

      // Draw scale pattern on top (darker)
      if (i % 3 === 0) {
        const scaleX = Math.cos(angle) * (segmentRadius + snakeWidth * 0.15);
        const scaleY = Math.sin(angle) * (segmentRadius + snakeWidth * 0.15);
        bodyG.circle(scaleX, scaleY, scaleSize);
        bodyG.fill({ color: SNAKE_SCALE_SPOT_COLOR, alpha: 0.6 });
      }
    }

    // Draw snake head (positioned at angle 0, right side of circle)
    const headAngle = 0;
    const headRadius = radius;
    const headX = Math.cos(headAngle) * headRadius;
    const headY = Math.sin(headAngle) * headRadius;

    // Head is larger and triangular
    const headLength = snakeWidth * 2;
    const headWidth = snakeWidth * 1.5;

    // Draw head shape (pointing outward)
    headG.moveTo(headX + headLength, headY);
    headG.lineTo(headX - headLength * 0.3, headY - headWidth);
    headG.lineTo(headX - headLength * 0.5, headY);
    headG.lineTo(headX - headLength * 0.3, headY + headWidth);
    headG.closePath();
    headG.fill({ color: snakePrimaryColor });

    // Draw head top scales
    headG.ellipse(headX + headLength * 0.3, headY, headLength * 0.4, headWidth * 0.6);
    headG.fill({ color: snakeSecondaryColor, alpha: 0.8 });

    // Draw eyes (menacing yellow eyes)
    const eyeOffsetX = headLength * 0.2;
    const eyeOffsetY = headWidth * 0.4;
    const eyeRadius = snakeWidth * 0.25;

    // Left eye
    headG.circle(headX + eyeOffsetX, headY - eyeOffsetY, eyeRadius);
    headG.fill({ color: snakeEyeColor });
    headG.circle(headX + eyeOffsetX + eyeRadius * 0.2, headY - eyeOffsetY, eyeRadius * 0.5);
    headG.fill({ color: 0x000000 }); // Pupil

    // Right eye
    headG.circle(headX + eyeOffsetX, headY + eyeOffsetY, eyeRadius);
    headG.fill({ color: snakeEyeColor });
    headG.circle(headX + eyeOffsetX + eyeRadius * 0.2, headY + eyeOffsetY, eyeRadius * 0.5);
    headG.fill({ color: 0x000000 }); // Pupil

    // Draw nostrils
    headG.circle(headX + headLength * 0.7, headY - headWidth * 0.2, 4);
    headG.fill({ color: 0x1a1a1a });
    headG.circle(headX + headLength * 0.7, headY + headWidth * 0.2, 4);
    headG.fill({ color: 0x1a1a1a });

    // Draw forked tongue (animated)
    const tongueExtension = Math.sin(this.snakeAnimTime * SNAKE_TONGUE_ANIMATION_SPEED) * 0.5 + 0.5;
    const tongueLength = headLength * 0.8 * tongueExtension;
    if (tongueExtension > 0.3) {
      headG.moveTo(headX + headLength, headY);
      headG.lineTo(headX + headLength + tongueLength, headY);
      headG.lineTo(headX + headLength + tongueLength + 15, headY - 10);
      headG.moveTo(headX + headLength + tongueLength, headY);
      headG.lineTo(headX + headLength + tongueLength + 15, headY + 10);
      headG.stroke({ width: 4, color: SNAKE_TONGUE_COLOR });
    }

    // Draw tail (opposite side of head, pointing inward then curling)
    const tailAngle = Math.PI; // Opposite to head
    const tailX = Math.cos(tailAngle) * radius;
    const tailY = Math.sin(tailAngle) * radius;

    // Tail tapers to a point
    for (let i = 0; i < SNAKE_TAIL_SEGMENTS; i++) {
      const tailProgress = i / SNAKE_TAIL_SEGMENTS;
      const tailWidth = snakeWidth * (1 - tailProgress * 0.8);
      const tailOffset = i * 15;
      const curveOffset = Math.sin(tailProgress * Math.PI) * 30;

      const tx = tailX - tailOffset - curveOffset;
      const ty = tailY + Math.sin(tailProgress * Math.PI * 2) * 20;

      bodyG.circle(tx, ty, tailWidth / 2);
      bodyG.fill({ color: i % 2 === 0 ? snakePrimaryColor : snakeSecondaryColor });
    }

    // Draw a subtle inner danger line (faint red to show boundary)
    borderG.circle(0, 0, radius - snakeWidth / 2);
    borderG.stroke({
      width: 2,
      color: WORLD_BORDER_COLOR,
      alpha: INNER_DANGER_LINE_ALPHA,
    });
  }

  /**
   * Draw the border glow (for warning effect)
   */
  private drawBorderGlow(): void {
    const { radius, borderGlowColor } = this.config;
    const g = this.borderGlowGraphics;
    g.clear();

    // Draw glow circle
    g.circle(0, 0, radius);
    g.stroke({
      width: WORLD_BORDER_THICKNESS * BORDER_GLOW_WIDTH_MULTIPLIER,
      color: borderGlowColor,
      alpha: 0,
    });
  }

  /**
   * Update border warning intensity based on player position
   * @param playerX Player X position
   * @param playerY Player Y position
   */
  updatePlayerPosition(playerX: number, playerY: number): void {
    const distFromCenter = Math.sqrt(playerX * playerX + playerY * playerY);
    this.playerDistanceFromCenter = distFromCenter;

    const { radius, warningDistance } = this.config;
    const dangerZoneStart = radius - warningDistance;

    if (distFromCenter > dangerZoneStart) {
      // Calculate warning intensity (0 to 1)
      this.warningIntensity = Math.min(
        1,
        (distFromCenter - dangerZoneStart) / warningDistance
      );
    } else {
      this.warningIntensity = 0;
    }
  }

  /**
   * Handle window resize
   */
  onResize(_width: number, _height: number): void {
    // World doesn't need to resize - it's fixed in world coordinates
    // The camera handles the viewport transformation
  }

  /**
   * Update world rendering
   * @param dt Delta time in seconds
   * @param zoom Current camera zoom level (optional)
   */
  update(dt: number, zoom?: number): void {
    // Animate the giant snake
    this.snakeAnimTime += dt;

    // Redraw snake periodically for animation (every ~100ms)
    if (Math.floor(this.snakeAnimTime * 10) !== Math.floor((this.snakeAnimTime - dt) * 10)) {
      this.drawGiantSnake();
    }

    // Animate border glow based on warning intensity
    this.updateBorderGlow(dt);

    // Update background ambiance (bubbles, stars, orbs)
    this.ambiance.update(dt);

    // Update grid scaling based on camera zoom
    if (zoom !== undefined) {
      this.updateGridScale(zoom);
    }
  }

  /**
   * Calculate and update grid scale based on camera zoom
   * When zoomed out, uses larger grid spacing to maintain visual clarity
   */
  private updateGridScale(zoom: number): void {
    // Calculate target grid scale level
    // At zoom 1.0: scale = 1 (100px grid)
    // At zoom 0.5: scale = 2 (200px grid)
    // At zoom 0.25: scale = 4 (400px grid)
    // At zoom 0.125: scale = 8 (800px grid)
    // This keeps the visual grid density roughly constant
    let targetScale = 1;
    if (zoom < 0.6) targetScale = 2;
    if (zoom < 0.35) targetScale = 4;
    if (zoom < 0.2) targetScale = 8;

    // Only redraw if scale level changed
    if (targetScale !== this.currentGridScale) {
      this.currentGridScale = targetScale;
      this.drawGrid();
    }
  }

  /**
   * Update border glow animation
   */
  private updateBorderGlow(_dt: number): void {
    if (this.warningIntensity <= 0) {
      this.borderGlowGraphics.alpha = 0;
      return;
    }

    // Pulsing glow effect
    const pulse = Math.sin(performance.now() / BORDER_GLOW_PULSE_PERIOD) * BORDER_GLOW_PULSE_AMPLITUDE + BORDER_GLOW_PULSE_CENTER;
    const alpha = this.warningIntensity * pulse;

    this.borderGlowGraphics.alpha = alpha;

    // Redraw glow with current intensity
    const { radius, borderGlowColor } = this.config;
    const g = this.borderGlowGraphics;
    g.clear();

    g.circle(0, 0, radius);
    g.stroke({
      width: WORLD_BORDER_THICKNESS * BORDER_GLOW_WIDTH_MULTIPLIER,
      color: borderGlowColor,
      alpha: alpha,
    });
  }

  /**
   * Get world radius
   */
  getRadius(): number {
    return this.config.radius;
  }

  /**
   * Get distance from player to world center
   */
  getPlayerDistanceFromCenter(): number {
    return this.playerDistanceFromCenter;
  }

  /**
   * Get current warning intensity
   */
  getWarningIntensity(): number {
    return this.warningIntensity;
  }

  /**
   * Check if a point is outside the world boundary
   */
  isOutOfBounds(x: number, y: number): boolean {
    return Math.sqrt(x * x + y * y) > this.config.radius;
  }

  /**
   * Get the distance from a point to the boundary (negative if outside)
   */
  getDistanceToBoundary(x: number, y: number): number {
    return this.config.radius - Math.sqrt(x * x + y * y);
  }

  /**
   * Clamp a position to be within the world boundary
   */
  clampToBoundary(x: number, y: number, padding: number = 0): { x: number; y: number } {
    const dist = Math.sqrt(x * x + y * y);
    const maxDist = this.config.radius - padding;

    if (dist > maxDist && dist > 0) {
      const scale = maxDist / dist;
      return { x: x * scale, y: y * scale };
    }

    return { x, y };
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.ambiance.destroy();
    this.gridGraphics.destroy();
    this.borderGraphics.destroy();
    this.borderGlowGraphics.destroy();
    this.snakeBodyGraphics.destroy();
    this.snakeHeadGraphics.destroy();
    this.container.destroy();
  }
}
